/**
 * AnimationManager - Handles GSAP animations and mobile interactions
 * Manages drag handles, panel animations, and responsive behaviors
 */
class AnimationManager {
    static CONFIG = {
        // Drag handle IDs
        PROJECTS_DRAG_HANDLE_ID: 'projects-drag-handle',
        FILTERS_DRAG_HANDLE_ID: 'filters-drag-handle',
        // Panel IDs
        PROJECTS_PANEL_ID: 'projects-panel',
        FILTERS_PANEL_ID: 'filters-panel',
        // Panel height settings (in em units)
        MIN_HEIGHT_EM: 20,
        SNAP_POSITION_PERCENT: 0.50, // 50% of range for snap position
        SNAP_POSITION_ACTIVE_PROJECT: 0.35, // 35% of range for snap position
        // Animation settings
        DRAG_DURATION: 0.5,
        DRAG_EASE: 'power2.out',
    };
    constructor() {
        // Reactive mobile detection
        this.isMobile = false;
        this._setupMobileMediaQuery();
        this.emUnit = parseFloat(getComputedStyle(document.documentElement).fontSize);
        // Draggable instances
        this.projectsDraggable = null;
        this.filtersDraggable = null;
        // Panel heights
        this.minHeight = AnimationManager.CONFIG.MIN_HEIGHT_EM * this.emUnit;
        this.maxHeight = Math.max(this.minHeight, window.innerHeight);
        this.heightRange = this.maxHeight - this.minHeight;
        this.snapPosition = this.minHeight + (this.heightRange * AnimationManager.CONFIG.SNAP_POSITION_PERCENT);
        this.snapPositionActiveProject = this.minHeight + (this.heightRange * AnimationManager.CONFIG.SNAP_POSITION_ACTIVE_PROJECT);
        this.snapHeights = [this.minHeight, this.snapPosition, this.maxHeight];
    }
    /**
     * Initialize all animations and mobile interactions
     * @public
     */
    initialize() {
        // Register GSAP plugins
        if (typeof gsap !== 'undefined' && gsap.registerPlugin) {
            gsap.registerPlugin(Draggable, InertiaPlugin);
        } else {
            return;
        }
        // Initialize mobile features if on mobile
        if (this.isMobile) {
            this._initializeMobileFeatures();
        }
        // Set initial states for project items
        this.setInitialProjectItemStates();
        this.setupProjectItemHoverAnimations();
    }
    /**
     * Setup reactive mobile detection with MediaQuery listener
     * Automatically updates this.isMobile when window is resized
     * @private
     */
    _setupMobileMediaQuery() {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        this.isMobile = mediaQuery.matches;
        // Listen for breakpoint changes
        mediaQuery.addEventListener('change', (e) => {
            this.isMobile = e.matches;
            if (this.isMobile) {
                this._initializeMobileFeatures();
            } else {
                this._destroyMobileFeatures();
            }
        });
    }
    /**
     * Initialize mobile-specific features
     * @private
     */
    _initializeMobileFeatures() {
        // Update height calculations based on current viewport
        this.maxHeight = Math.max(this.minHeight, window.innerHeight);
        this.heightRange = this.maxHeight - this.minHeight;
        this.snapPosition = this.minHeight + (this.heightRange * AnimationManager.CONFIG.SNAP_POSITION_PERCENT);
        this.snapPositionActiveProject = this.minHeight + (this.heightRange * AnimationManager.CONFIG.SNAP_POSITION_ACTIVE_PROJECT);
        this.snapHeights = [this.minHeight, this.snapPosition, this.maxHeight];
        // Setup drag handles
        this._setupProjectsDragHandle();
        this._setupFiltersDragHandle();
    }
    /**
     * Destroy mobile features (clean up on desktop switch)
     * @private
     */
    _destroyMobileFeatures() {
        // Kill draggable instances
        if (this.projectsDraggable) {
            this.projectsDraggable.kill();
            this.projectsDraggable = null;
        }
        if (this.filtersDraggable) {
            this.filtersDraggable.kill();
            this.filtersDraggable = null;
        }
        // Reset panel heights
        const projectsPanel = document.getElementById(AnimationManager.CONFIG.PROJECTS_PANEL_ID);
        const filtersPanel = document.getElementById(AnimationManager.CONFIG.FILTERS_PANEL_ID);
        if (projectsPanel) projectsPanel.style.height = '';
        if (filtersPanel) filtersPanel.style.height = '';
    }
    /**
     * Setup projects panel drag handle
     * @private
     */
    _setupProjectsDragHandle() {
        const handle = document.getElementById(AnimationManager.CONFIG.PROJECTS_DRAG_HANDLE_ID);
        const panel = document.getElementById(AnimationManager.CONFIG.PROJECTS_PANEL_ID);
        if (!handle || !panel) {
            return;
        }
        // Initialize panel to minimum height
        gsap.set(panel, { height: `${this.minHeight}px` });
        const SNAP_THRESHOLD = 60; // Snap when within 60px of snap point
        const CLOSE_THRESHOLD = 50; // Close panel if dragged this far below minHeight
        // Capture snap heights in closure
        const snapHeights = this.snapHeights;
        const minHeight = this.minHeight;
        const maxHeight = this.maxHeight;
        const snapPosition = this.snapPosition;
        let startHeight = 0;
        let startY = 0;
        // Kill existing draggable if it exists
        if (this.projectsDraggable) {
            this.projectsDraggable.kill();
        }
        // Create draggable
        this.projectsDraggable = Draggable.create(panel, {
            trigger: handle,
            type: 'y',
            inertia: true,
            onPress: function (e) {
                startHeight = panel.offsetHeight;
                startY = e.clientY || e.touches[0].clientY;
            },
            onDrag: function (e) {
                const currentY = e.clientY || e.touches[0].clientY;
                const deltaY = currentY - startY;
                const newHeight = startHeight - deltaY;
                // Allow dragging below minHeight for dismiss gesture
                const clampedHeight = gsap.utils.clamp(
                    minHeight - CLOSE_THRESHOLD,
                    maxHeight,
                    newHeight
                );
                // Set height directly without snapping during drag
                gsap.set(panel, { height: `${clampedHeight}px` });
                gsap.set(this.target, { y: 0 });
            },
            onDragEnd: function () {
                gsap.set(this.target, { y: 0 });
                const currentHeight = panel.offsetHeight;
                // If dragged below minHeight threshold, close the panel
                if (currentHeight < minHeight) {
                    gsap.to(panel, {
                        height: 0,
                        opacity: 0,
                        duration: AnimationManager.CONFIG.DRAG_DURATION * 0.6,
                        ease: AnimationManager.CONFIG.DRAG_EASE,
                        onComplete: () => {
                            // Reset and close panel via UIManager
                            gsap.set(panel, { height: `${minHeight}px`, opacity: 1 });
                            if (window.uiManager && window.uiManager.closeProjectsPanel) {
                                window.uiManager.closeProjectsPanel();
                            }
                        }
                    });
                    return;
                }
                // Snap to closest height with animation
                // Increase threshold for maxHeight to make it easier to snap to top
                let closestHeight = snapHeights[0];
                let minDistance = Math.abs(currentHeight - snapHeights[0]);
                for (let i = 1; i < snapHeights.length; i++) {
                    const distance = Math.abs(currentHeight - snapHeights[i]);
                    // Make maxHeight snap easier - increase its threshold
                    let adjustedDistance = distance;
                    if (snapHeights[i] === maxHeight) {
                        // If within 150px of maxHeight, make it much more attractive to snap to
                        if (distance < 150) {
                            adjustedDistance = distance * 0.3; // Make it 70% more attractive
                        }
                    }
                    if (adjustedDistance < minDistance) {
                        minDistance = adjustedDistance;
                        closestHeight = snapHeights[i];
                    }
                }
                gsap.to(panel, {
                    height: `${closestHeight}px`,
                    duration: AnimationManager.CONFIG.DRAG_DURATION,
                    ease: AnimationManager.CONFIG.DRAG_EASE
                });
            }
        })[0];
        // Handle click to toggle between heights
        handle.addEventListener('click', () => {
            const currentHeight = panel.offsetHeight;
            let targetHeight;
            // Define a tolerance for comparing heights (within 10px)
            const tolerance = 10;
            if (Math.abs(currentHeight - minHeight) < tolerance) {
                // At initial height -> go to mid
                targetHeight = snapPosition;
            } else if (Math.abs(currentHeight - snapPosition) < tolerance) {
                // At mid height -> go to initial
                targetHeight = minHeight;
            } else if (Math.abs(currentHeight - maxHeight) < tolerance) {
                // At max height -> go to initial
                targetHeight = minHeight;
            } else {
                // Anywhere else -> go to mid
                targetHeight = snapPosition;
            }
            gsap.to(panel, {
                height: `${targetHeight}px`,
                duration: AnimationManager.CONFIG.DRAG_DURATION,
                ease: AnimationManager.CONFIG.DRAG_EASE
            });
        });
    }
    /**
     * Setup filters panel drag handle
     * Drags panel down and reduces opacity, closes when dragged more than 12px
     * Height never changes
     * @private
     */
    _setupFiltersDragHandle() {
        const handle = document.getElementById(AnimationManager.CONFIG.FILTERS_DRAG_HANDLE_ID);
        const panel = document.getElementById(AnimationManager.CONFIG.FILTERS_PANEL_ID);
        if (!handle || !panel) {
            return;
        }
        const CLOSE_THRESHOLD = 8; // Close when dragged more than 8px down
        let currentDragY = 0;
        // Kill existing draggable if it exists
        if (this.filtersDraggable) {
            this.filtersDraggable.kill();
        }
        // Create draggable (vertical position and opacity change)
        this.filtersDraggable = Draggable.create(panel, {
            trigger: handle,
            type: 'y',
            bounds: { minY: 0, maxY: 100 }, // Allow dragging down up to 100px
            onDragStart: function () {
                currentDragY = 0;
            },
            onDrag: function () {
                currentDragY = this.y;
                // Only allow dragging down (positive y)
                if (currentDragY < 0) {
                    gsap.set(this.target, { y: 0 });
                    currentDragY = 0;
                    return;
                }
                // Calculate opacity based on drag distance (0 to 100px range)
                const opacity = Math.max(0, 1 - (currentDragY / 50));
                gsap.set(panel, { opacity: opacity });
            },
            onDragEnd: function () {
                // If dragged more than 12px, close the panel
                if (currentDragY > CLOSE_THRESHOLD) {
                    // Animate panel down and fade out
                    gsap.to(panel, {
                        y: 50,
                        opacity: 0,
                        duration: AnimationManager.CONFIG.DRAG_DURATION * 0.6,
                        ease: AnimationManager.CONFIG.DRAG_EASE,
                        onComplete: () => {
                            // Reset position and trigger panel close
                            gsap.set(panel, { y: 0, opacity: 1 });
                            if (window.uiManager && window.uiManager.closeFiltersPanel) {
                                window.uiManager.closeFiltersPanel();
                            }
                        }
                    });
                } else {
                    // Snap back to original position
                    gsap.to(panel, {
                        y: 0,
                        opacity: 1,
                        duration: AnimationManager.CONFIG.DRAG_DURATION * 0.5,
                        ease: 'power2.out'
                    });
                }
            }
        })[0];
        // Handle click to close panel (no animation, just deactivate)
        handle.addEventListener('click', () => {
            if (window.uiManager && window.uiManager.closeFiltersPanel) {
                window.uiManager.closeFiltersPanel();
            }
        });
    }
    /**
     * Animate panel to specific height
     * @param {string} panelId - Panel element ID
     * @param {number} height - Target height in pixels
     * @param {number} duration - Animation duration (optional)
     * @public
     */
    animatePanelHeight(panelId, height, duration = AnimationManager.CONFIG.DRAG_DURATION) {
        const panel = document.getElementById(panelId);
        if (!panel) {
            return;
        }
        gsap.to(panel, {
            height: `${height}px`,
            duration: duration,
            ease: AnimationManager.CONFIG.DRAG_EASE
        });
    }
    /**
     * Get snap heights for external use
     * @returns {Object} Object with minHeight, snapPosition, maxHeight
     * @public
     */
    getSnapHeights() {
        return {
            minHeight: this.minHeight,
            snapPosition: this.snapPosition,
            maxHeight: this.maxHeight
        };
    }
    /**
     * Check if mobile mode is active
     * @returns {boolean}
     * @public
     */
    isMobileMode() {
        return this.isMobile;
    }
    /**
     * Snap projects panel to active project height
     * Called when a project is activated on mobile
     * @public
     */
    snapToActiveProjectHeight() {
        if (!this.isMobile) return;
        const panel = document.getElementById(AnimationManager.CONFIG.PROJECTS_PANEL_ID);
        if (!panel) return;
        gsap.to(panel, {
            height: `${this.snapPositionActiveProject}px`,
            duration: AnimationManager.CONFIG.DRAG_DURATION,
            ease: AnimationManager.CONFIG.DRAG_EASE
        });
    }
    /**
     * Set initial GSAP states for project items
     * @public
     */
    setInitialProjectItemStates() {
        // Query all project items
        const projectItems = document.querySelectorAll('.project');
        projectItems.forEach(item => {
            // Only apply to inactive items
            if (!item.classList.contains('is--active')) {
                // Typology bar
                const typologyBar = item.querySelector('.typology__bar');
                if (typologyBar) {
                    gsap.set(typologyBar, { width: '0%' });
                }
                // Button wrapper (plus)
                const buttonWrapper = item.querySelector('.button__wrapper[project-item-button="plus"]');
                if (buttonWrapper) {
                    gsap.set(buttonWrapper, { autoAlpha: 0 });
                }
                // Project image
                const projectImage = item.querySelector('.project__image');
                if (projectImage) {
                    gsap.set(projectImage, { scale: 1 });
                }
            }
        });
    }
    /**
     * Set up GSAP hover animations for project items
     * @public
     */
    setupProjectItemHoverAnimations() {
        const projectItems = document.querySelectorAll('.project');
        projectItems.forEach(item => {
            // Only apply to inactive items
            if (!item.classList.contains('is--active')) {
                const typologyBar = item.querySelector('.typology__bar');
                const buttonWrapper = item.querySelector('.button__wrapper[project-item-button="plus"]');
                const projectImage = item.querySelector('.project__image');
                // Guard: must have all elements
                if (!typologyBar || !buttonWrapper || !projectImage) return;
                // Mouse enter
                item.addEventListener('mouseenter', () => {
                    if (item.classList.contains('is--active')) return; // Prevent if active
                    gsap.to(typologyBar, { width: '100%', duration: 0.5, ease: 'power2.out' });
                    gsap.to(projectImage, { scale: 1.1, duration: 0.5, ease: 'power2.out' });
                    gsap.to(buttonWrapper, { autoAlpha: 1, duration: 0.4, ease: 'power2.out' });
                });
                // Mouse leave
                item.addEventListener('mouseleave', () => {
                    if (item.classList.contains('is--active')) return; // Prevent if active
                    gsap.to(typologyBar, { width: '0%', duration: 0.5, ease: 'power2.in' });
                    gsap.to(projectImage, { scale: 1, duration: 0.5, ease: 'power2.in' });
                    gsap.to(buttonWrapper, { autoAlpha: 0, duration: 0.4, ease: 'power2.in' });
                });
            }
        });
    }
}
// ! EXPORT
export default AnimationManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') {
    window.AnimationManager = AnimationManager;
}